import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthenticationService } from '../services/authentication.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    ReactiveFormsModule
  ]
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthenticationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  credentialForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]]
  });

  accountErrorMessage?: string;
  formSubmitted = false;
  redirectUrl?: string | null;
  eventID?: string | null;

  ngOnInit(): void {
    this.authService.redirectLoggedInUser();

    this.route.queryParamMap.subscribe(params => {
      this.redirectUrl = params.get('redirect');
      this.eventID = params.get('id');
    });
  }

  /**
   * Attempts to sign in user with the details provided in the login form.
   */
  async signIn() {
    // Exits out of function if the credential form has errors.
    if (!this.credentialForm.valid) {
      this.formSubmitted = true;
      return;
    }

    try {
      const { email, password } = this.credentialForm.value;
      const res = await this.authService.signIn({ email: email!, password: password! });
      if (!res.user) return;

      this.authService.getUserDetails(res.user.uid).subscribe(async () => {
        this.credentialForm.reset();

        const target = this.redirectUrl
          ? this.eventID
            ? `/${this.redirectUrl}/${this.eventID}`
            : `/${this.redirectUrl}`
          : '/home';

        await this.router.navigate([target]);
      });

    } catch (error: any) {
      this.accountErrorMessage = this.getErrorMessage(error?.code);
    }
  }

  private getErrorMessage(code: string): string {
    switch (code) {
      case 'auth/invalid-email':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'De combinatie van e-mailadres en wachtwoord is niet geldig.';
      case 'auth/too-many-requests':
        return 'Dit account is tijdelijk uitgeschakeld omdat er te vaak is geprobeerd om in te loggen met foute gegevens. Probeer het later opnieuw.';
      default:
        return 'Onverwachte error. Probeer het later opnieuw.';
    }
  }

  /**
   * @return Filled in email value on login form.
   */
  get email() {
    return this.credentialForm.get('email');
  }

  /**
   * @return Filled in password value on login form.
   */
  get password() {
    return this.credentialForm.get('password');
  }
}
